
import React from 'react';
import { useTheme } from "@/contexts/ThemeContext";

interface ExpenseBarChartProps {
  value: number;
  maxValue: number;
  className?: string;
  shouldAnimate?: boolean;
}

export const ExpenseBarChart = ({ 
  value, 
  maxValue, 
  className = "",
  shouldAnimate = false 
}: ExpenseBarChartProps) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  // Calculate percentage width (minimum 5% for visibility when value > 0)
  const percentage = maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 5 : 0) : 0;
  
  // Generate unique animation delay for staggered effect
  const animationDelay = shouldAnimate ? Math.random() * 300 : 0;
  
  // Determine color based on expense amount
  const getBarColor = (amount: number) => {
    if (amount >= 1000) {
      return isLightMode ? 'bg-green-500' : 'bg-green-600'; // Green for 1k+
    } else if (amount >= 501) {
      return isLightMode ? 'bg-green-300' : 'bg-green-400'; // Subtle green for 501-1k
    } else if (amount > 0) {
      return isLightMode ? 'bg-green-100' : 'bg-green-200'; // Light green for 0-500
    }
    return isLightMode ? 'bg-gray-300' : 'bg-gray-600'; // Gray for 0
  };
  
  const barColor = getBarColor(value);
  
  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Background bar */}
      <div className={`absolute inset-0 rounded overflow-hidden ${isLightMode ? 'bg-gray-100' : 'bg-gray-800'}`} />
      
      {/* Progress bar with color categorization and subtle stripes */}
      <div 
        className={`absolute left-0 top-0 h-full rounded overflow-hidden transition-all duration-1000 ease-out ${barColor}`}
        style={{ 
          width: shouldAnimate ? `${percentage}%` : `${percentage}%`,
          backgroundImage: isLightMode 
            ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.15) 8px,
                rgba(255, 255, 255, 0.15) 16px
              )`
            : `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 8px,
                rgba(255, 255, 255, 0.1) 8px,
                rgba(255, 255, 255, 0.1) 16px
              )`,
          animation: shouldAnimate 
            ? `growWidthExpense 1000ms ease-out ${animationDelay}ms both, moveStripesExpense 3s linear infinite`
            : 'none'
        }}
      />
      
      {/* Value text */}
      <div className={`relative z-10 px-3 py-1 font-medium ${
        isLightMode ? 'text-gray-900' : 'text-gray-100'
      }`}>
        ${value.toLocaleString()}
      </div>
      
      {/* Global CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes growWidthExpense {
            from {
              width: 0%;
            }
            to {
              width: ${percentage}%;
            }
          }
          
          @keyframes moveStripesExpense {
            0% {
              background-position: 0 0;
            }
            100% {
              background-position: 32px 0;
            }
          }
        `
      }} />
    </div>
  );
};
